// src/lib/staffCode.ts
//
// Generating a persistable staff code for a newly created staff member.
//
// WHY THIS MATTERS (issue #204): staff_code is the allocation key. Grids, load
// letters, preference matching and the own-row RLS policies on
// teaching_intentions all join on staff.staff_code. A staff member created
// without one is invisible to all of that — and worse, DataContext used to
// invent a display-only code at read time which then got written into
// teaching_intentions.staff_id, producing rows that attach to nobody and that
// the teacher themselves cannot see under RLS.
//
// So the code must be chosen once, at creation, and persisted.
//
// HOUSE CONVENTION, reverse-engineered from the live roster:
//   first initial + first letter of surname + a subsequent letter of surname
//     Loversidge -> SLO   Collins  -> FCO   Higham -> JHI
//     Shaw       -> LSH   Robson   -> SRO   Fattore -> TFA
//   When the obvious form is taken, the third letter walks further along the
//   surname rather than gaining a digit:
//     Cautley  -> MCA taken -> MCU, MCT, MCL, MCE, MCY
//     McCorry  -> BMC taken -> BMO, BMR, BMY
// Only once every letter is exhausted do we fall back to a numeric suffix.
//
// Uniqueness is ultimately enforced by the unique index
// `uq_pit_staff_tenant_staff_code` on (tenant_id, staff_code). This module only
// proposes candidates in preference order; the caller must walk the list and
// let the database arbitrate, so two admins adding staff at the same moment
// cannot both win the same code.

const MAX_NUMERIC_FALLBACK = 99;

/** Strip anything that isn't a letter — apostrophes, hyphens, accents on O'Connell / Barnard-Whelan. */
const lettersOnly = (value: string): string => value.replace(/[^A-Za-z]/g, '');

/**
 * Candidate staff codes for `name`, best first.
 *
 * Returns an empty array if the name has no usable letters at all, which the
 * caller must treat as "cannot assign a code" rather than inventing one.
 */
export function staffCodeCandidates(name: string): string[] {
  const parts = (name || '').trim().split(/\s+/).map(lettersOnly).filter(Boolean);
  if (parts.length === 0) return [];

  const first = parts[0];
  // Single-name staff have no surname to walk, so use their one name for both.
  const surname = parts.length > 1 ? parts[parts.length - 1] : first;

  const initial = first[0].toUpperCase();
  const surnameUpper = surname.toUpperCase();

  const candidates: string[] = [];
  const push = (code: string) => {
    if (code.length === 3 && !candidates.includes(code)) candidates.push(code);
  };

  // Walk the surname: initial + surname[0] + surname[i] for i = 1, 2, 3...
  for (let i = 1; i < surnameUpper.length; i++) {
    push(initial + surnameUpper[0] + surnameUpper[i]);
  }

  // Surnames of one letter leave nothing to walk — fall back to the first name.
  if (candidates.length === 0) {
    const firstUpper = first.toUpperCase();
    for (let i = 1; i < firstUpper.length; i++) {
      push(initial + firstUpper[0] + firstUpper[i]);
    }
  }

  // Last resort: two letters plus a digit. Deliberately after every letter
  // combination, because a digit reads as wrong against this school's roster.
  const stem = (initial + surnameUpper[0]).padEnd(2, initial);
  for (let n = 2; n <= MAX_NUMERIC_FALLBACK; n++) {
    const code = stem + n;
    if (!candidates.includes(code)) candidates.push(code);
  }

  return candidates;
}

/**
 * First candidate not already present in `taken`.
 *
 * `taken` should hold the codes currently known to the client. It is only an
 * optimisation to avoid obvious collisions — the unique index is what actually
 * guarantees correctness, so the caller still has to handle a 23505 and move on
 * to the next candidate.
 */
export function pickStaffCode(name: string, taken: Iterable<string>): string | null {
  const used = new Set(
    Array.from(taken)
      .filter(Boolean)
      .map(code => code.toUpperCase())
  );
  return staffCodeCandidates(name).find(code => !used.has(code)) ?? null;
}
